import React, { useState, useEffect } from 'react';
import { BookOpen, Plus, Search, Edit3, Trash2, Star, TrendingUp, Calendar, DollarSign } from 'lucide-react';
import { questionBankService, QuestionBank as FirebaseQuestionBank } from '../services/questionBankService';
import { getCurrentUser } from '../services/firebase';

// Basit soru bankası arayüzü (Firebase ile uyumlu)
interface QuestionBank {
  id: string;
  name: string;
  subject: string;
  publisher: string;
  totalQuestions: number;
  completedQuestions: number;
  price: number;
  isFavorite: boolean;
  color: string;
  userId?: string;
  createdAt?: string;
  updatedAt?: string;
}

// 8. sınıf LGS dersleri (Resmi ders adları)
const LGS_SUBJECTS = [
  { key: 'turkce', name: 'Türkçe', color: 'bg-red-500' },
  { key: 'matematik', name: 'Matematik', color: 'bg-blue-500' },
  { key: 'fen', name: 'Fen Bilimleri', color: 'bg-green-500' },
  { key: 'inkilap', name: 'T.C. İnkılâp Tarihi ve Atatürkçülük', color: 'bg-purple-500' },
  { key: 'yabanci_dil', name: 'Yabancı Dil', color: 'bg-yellow-500' },
  { key: 'din', name: 'Din Kültürü ve Ahlak Bilgisi', color: 'bg-indigo-500' }
];

// Popüler yayınevleri
const PUBLISHERS = [
  'Karekök Yayınları',
  'Palme Yayınları', 
  'Bilfen Yayınları',
  'Tudem Yayınları',
  'Hız Yayınları',
  'Editör Yayınları',
  'Diğer'
];

const QuestionBank: React.FC = () => {
  const [questionBanks, setQuestionBanks] = useState<QuestionBank[]>([]);
  const [filteredBanks, setFilteredBanks] = useState<QuestionBank[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('Tümü');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingBank, setEditingBank] = useState<QuestionBank | null>(null);
  
  // Yeni soru bankası formu
  const [newBank, setNewBank] = useState({
    name: '',
    subject: 'turkce',
    publisher: 'Karekök Yayınları',
    totalQuestions: '',
    price: ''
  });
  
  // Özel yayın evi adı için state
  const [customPublisher, setCustomPublisher] = useState('');

  // Firebase'den veri yükleme
  useEffect(() => {
    const loadQuestionBanks = async () => {
      try {
        setLoading(true);
        const user = getCurrentUser();
        if (user) {
          const banks = await questionBankService.getQuestionBanks(user.uid);
          const mappedBanks: QuestionBank[] = banks.map(bank => ({
            id: bank.id,
            name: bank.name,
            subject: bank.subject,
            publisher: bank.publisher,
            totalQuestions: bank.totalQuestions,
            completedQuestions: bank.completedQuestions || 0,
            price: bank.price,
            isFavorite: bank.isFavorite || false,
            color: LGS_SUBJECTS.find(s => s.key === bank.subject)?.color || 'bg-gray-500',
            userId: bank.userId,
            createdAt: bank.createdAt,
            updatedAt: bank.updatedAt
          }));
          setQuestionBanks(mappedBanks);
          setFilteredBanks(mappedBanks);
        }
      } catch (err) {
        setError('Soru bankaları yüklenirken hata oluştu');
        console.error('Error loading question banks:', err);
      } finally {
        setLoading(false);
      }
    };

    loadQuestionBanks();
  }, []);

  // Filtreleme ve arama
  useEffect(() => {
    let filtered = questionBanks;
    
    // Ders filtresi
    if (selectedSubject !== 'Tümü') {
      filtered = filtered.filter(bank => bank.subject === selectedSubject);
    }
    
    // Arama filtresi
    if (searchTerm) {
      filtered = filtered.filter(bank => 
        bank.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bank.publisher.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredBanks(filtered);
  }, [questionBanks, selectedSubject, searchTerm]);

  // Yeni soru bankası ekleme
  const handleAddBank = async () => {
    if (!newBank.name || !newBank.totalQuestions || !newBank.price) {
      alert('Lütfen tüm alanları doldurun!');
      return;
    }
    
    // Diğer seçildiğinde özel yayın evi adı kontrolü
    if (newBank.publisher === 'Diğer' && !customPublisher.trim()) {
      alert('Lütfen yayın evi adını girin!');
      return;
    }

    try {
      const user = getCurrentUser();
      if (!user) {
        alert('Lütfen giriş yapın!');
        return;
      }

      const subjectData = LGS_SUBJECTS.find(s => s.key === newBank.subject);
      
      const bankData: Omit<FirebaseQuestionBank, 'id'> = {
        name: newBank.name,
        subject: newBank.subject,
        publisher: newBank.publisher === 'Diğer' ? customPublisher : newBank.publisher,
        totalQuestions: parseInt(newBank.totalQuestions),
        completedQuestions: 0,
        price: parseFloat(newBank.price),
        isFavorite: false,
        userId: user.uid,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      const newBankId = await questionBankService.addQuestionBank(bankData);
      
      const bank: QuestionBank = {
        id: newBankId,
        ...bankData,
        color: subjectData?.color || 'bg-gray-500'
      };
      
      setQuestionBanks([...questionBanks, bank]);
      setNewBank({
        name: '',
        subject: 'turkce',
        publisher: 'Karekök Yayınları',
        totalQuestions: '',
        price: ''
      });
      setCustomPublisher('');
      setShowAddModal(false);
    } catch (error) {
      console.error('Error adding question bank:', error);
      alert('Soru bankası eklenirken hata oluştu!');
    }
  };

  // Soru bankası silme
  const handleDeleteBank = async (id: string) => {
    if (window.confirm('Bu soru bankasını silmek istediğinizden emin misiniz?')) {
      try {
        await questionBankService.deleteQuestionBank(id);
        setQuestionBanks(questionBanks.filter(bank => bank.id !== id));
      } catch (error) {
        console.error('Error deleting question bank:', error);
        alert('Soru bankası silinirken hata oluştu!');
      }
    }
  };

  // Soru bankası düzenleme
  const handleEditBank = async () => {
    if (!editingBank || !newBank.name || !newBank.totalQuestions || !newBank.price) {
      alert('Lütfen tüm alanları doldurun!');
      return;
    }
    
    // Diğer seçildiğinde özel yayın evi adı kontrolü
    if (newBank.publisher === 'Diğer' && !customPublisher.trim()) {
      alert('Lütfen yayın evi adını girin!');
      return;
    }

    try {
      const subjectData = LGS_SUBJECTS.find(s => s.key === newBank.subject);
      
      const updatedData: Partial<FirebaseQuestionBank> = {
        name: newBank.name,
        subject: newBank.subject,
        publisher: newBank.publisher === 'Diğer' ? customPublisher : newBank.publisher,
        totalQuestions: parseInt(newBank.totalQuestions),
        price: parseFloat(newBank.price),
        updatedAt: new Date().toISOString()
      };
      
      await questionBankService.updateQuestionBank(editingBank.id, updatedData);
      
      const updatedBank: QuestionBank = {
        ...editingBank,
        ...updatedData,
        color: subjectData?.color || 'bg-gray-500'
      };
      
      setQuestionBanks(questionBanks.map(bank => 
        bank.id === editingBank.id ? updatedBank : bank
      ));
      
      setNewBank({
        name: '',
        subject: 'turkce',
        publisher: 'Karekök Yayınları',
        totalQuestions: '',
        price: ''
      });
      setCustomPublisher('');
      setEditingBank(null);
    } catch (error) {
      console.error('Error updating question bank:', error);
      alert('Soru bankası güncellenirken hata oluştu!');
    }
  };

  // Favorilere ekleme/çıkarma
  const toggleFavorite = async (id: string) => {
    try {
      const bank = questionBanks.find(b => b.id === id);
      if (!bank) return;
      
      const updatedData = {
        isFavorite: !bank.isFavorite,
        updatedAt: new Date().toISOString()
      };
      
      await questionBankService.updateQuestionBank(id, updatedData);
      
      setQuestionBanks(questionBanks.map(bank => 
        bank.id === id ? { ...bank, ...updatedData } : bank
      ));
    } catch (error) {
      console.error('Error toggling favorite:', error);
      alert('Favori durumu güncellenirken hata oluştu!');
    }
  };

  // İlerleme güncelleme
  const updateProgress = async (id: string, completedQuestions: number) => {
    try {
      const updatedData = {
        completedQuestions,
        updatedAt: new Date().toISOString()
      };
      
      await questionBankService.updateQuestionBank(id, updatedData);
      
      setQuestionBanks(questionBanks.map(bank => 
        bank.id === id ? { ...bank, ...updatedData } : bank
      ));
    } catch (error) {
      console.error('Error updating progress:', error);
      alert('İlerleme güncellenirken hata oluştu!');
    }
  };

  // Ders adını getir
  const getSubjectName = (subjectKey: string) => {
    return LGS_SUBJECTS.find(s => s.key === subjectKey)?.name || subjectKey;
  };

  return (
    <div className="min-h-screen p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white/10 dark:bg-gray-800/20 backdrop-blur-sm rounded-2xl shadow-lg p-6 mb-6 border border-white/20 dark:border-gray-700/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Soru Bankalarım</h1>
                <p className="text-gray-600 dark:text-gray-300">LGS hazırlık soru bankalarını yönet</p>
              </div>
            </div>
            
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all duration-200 shadow-lg"
            >
              <Plus className="w-4 h-4" />
              Yeni Soru Bankası
            </button>
          </div>
        </div>

        {/* Arama ve Filtreler */}
        <div className="bg-white/10 dark:bg-gray-800/20 backdrop-blur-sm rounded-2xl shadow-lg p-6 mb-6 border border-white/20 dark:border-gray-700/30">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Arama */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Soru bankası ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            
            {/* Ders Filtresi */}
            <select
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="Tümü">Tüm Dersler</option>
              {LGS_SUBJECTS.map(subject => (
                <option key={subject.key} value={subject.key}>{subject.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* İstatistikler */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white/10 dark:bg-gray-800/20 backdrop-blur-sm rounded-xl p-4 border border-white/20 dark:border-gray-700/30">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <BookOpen className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-300">Toplam Kitap</p>
                <p className="text-xl font-bold text-gray-800 dark:text-white">{questionBanks.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white/10 dark:bg-gray-800/20 backdrop-blur-sm rounded-xl p-4 border border-white/20 dark:border-gray-700/30">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-300">Ortalama İlerleme</p>
                <p className="text-xl font-bold text-gray-800 dark:text-white">
                  {questionBanks.length > 0 
                    ? Math.round(questionBanks.reduce((acc, bank) => acc + ((bank.completedQuestions / bank.totalQuestions) * 100), 0) / questionBanks.length)
                    : 0}%
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white/10 dark:bg-gray-800/20 backdrop-blur-sm rounded-xl p-4 border border-white/20 dark:border-gray-700/30">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Star className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-300">Favoriler</p>
                <p className="text-xl font-bold text-gray-800 dark:text-white">
                  {questionBanks.filter(bank => bank.isFavorite).length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white/10 dark:bg-gray-800/20 backdrop-blur-sm rounded-xl p-4 border border-white/20 dark:border-gray-700/30">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <DollarSign className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-300">Toplam Harcama</p>
                <p className="text-xl font-bold text-gray-800 dark:text-white">
                  ₺{questionBanks.reduce((acc, bank) => acc + bank.price, 0).toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-300">Soru bankaları yükleniyor...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-100 dark:bg-red-900/20 border border-red-400 dark:border-red-600 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg mb-6">
            <p>{error}</p>
          </div>
        )}

        {/* Soru Bankası Kartları */}
        {!loading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBanks.map((bank) => (
            <div key={bank.id} className="bg-white/10 dark:bg-gray-800/20 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 dark:border-gray-700/30 overflow-hidden hover:shadow-xl transition-all duration-200">
              {/* Kart Header */}
              <div className={`${bank.color} p-4 text-white relative`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-5 h-5" />
                    <span className="font-semibold">{getSubjectName(bank.subject)}</span>
                  </div>
                  <button
                    onClick={() => toggleFavorite(bank.id)}
                    className={`p-1 rounded-full transition-colors ${
                      bank.isFavorite ? 'bg-yellow-400 text-yellow-800' : 'bg-white/20 text-white'
                    }`}
                  >
                    <Star className="w-4 h-4" fill={bank.isFavorite ? 'currentColor' : 'none'} />
                  </button>
                </div>
              </div>
              
              {/* Kart İçerik */}
              <div className="p-4">
                <h3 className="font-bold text-gray-800 dark:text-white mb-2 line-clamp-2">{bank.name}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">{bank.publisher}</p>
                
                {/* İstatistikler */}
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-300">Soru Sayısı:</span>
                    <span className="font-semibold">{bank.totalQuestions.toLocaleString()}</span>
                  </div>
                  
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-300">Fiyat:</span>
                    <span className="font-semibold text-green-600">₺{bank.price.toFixed(2)}</span>
                  </div>
                  
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-300">Oluşturma:</span>
                    <span className="font-semibold">{bank.createdAt ? new Date(bank.createdAt).toLocaleDateString('tr-TR') : '-'}</span>
                  </div>
                  
                  {/* İlerleme Çubuğu */}
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600 dark:text-gray-300">İlerleme:</span>
                      <span className="font-semibold">{Math.round((bank.completedQuestions / bank.totalQuestions) * 100)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${Math.round((bank.completedQuestions / bank.totalQuestions) * 100)}%` }}
                      ></div>
                    </div>
                  </div>
                  
                  {/* İlerleme Güncelleme */}
                  <div className="flex gap-2">
                    <input
                      type="range"
                      min="0"
                      max={bank.totalQuestions}
                      value={bank.completedQuestions}
                      onChange={(e) => updateProgress(bank.id, parseInt(e.target.value))}
                      className="flex-1"
                    />
                  </div>
                </div>
                
                {/* Aksiyon Butonları */}
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => {
                      setEditingBank(bank);
                      setNewBank({
                        name: bank.name,
                        subject: bank.subject,
                        publisher: bank.publisher,
                        totalQuestions: bank.totalQuestions.toString(),
                        price: bank.price.toString()
                      });
                      setShowAddModal(true);
                    }}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                  >
                    <Edit3 className="w-4 h-4" />
                    Düzenle
                  </button>
                  <button
                    onClick={() => handleDeleteBank(bank.id)}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    Sil
                  </button>
                </div>
              </div>
            </div>
            ))}
          </div>
        )}

        {/* Boş Durum */}
        {filteredBanks.length === 0 && (
          <div className="text-center py-12">
            <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-600 dark:text-gray-300 mb-2">Soru bankası bulunamadı</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">Arama kriterlerinizi değiştirin veya yeni bir soru bankası ekleyin.</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              İlk Soru Bankasını Ekle
            </button>
          </div>
        )}
      </div>

      {/* Soru Bankası Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl p-4 sm:p-6 w-full max-w-[95vw] sm:max-w-md md:max-w-lg max-h-[95vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4">
              {editingBank ? 'Soru Bankasını Düzenle' : 'Yeni Soru Bankası Ekle'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Kitap Adı</label>
                <input
                  type="text"
                  value={newBank.name}
                  onChange={(e) => setNewBank({...newBank, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Örn: LGS Matematik Soru Bankası"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ders</label>
                <select
                  value={newBank.subject}
                  onChange={(e) => setNewBank({...newBank, subject: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {LGS_SUBJECTS.map(subject => (
                    <option key={subject.key} value={subject.key}>{subject.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Yayınevi</label>
                <select
                  value={newBank.publisher}
                  onChange={(e) => {
                    setNewBank({...newBank, publisher: e.target.value});
                    if (e.target.value !== 'Diğer') {
                      setCustomPublisher('');
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {PUBLISHERS.map(publisher => (
                    <option key={publisher} value={publisher}>{publisher}</option>
                  ))}
                </select>
                
                {/* Özel yayın evi input alanı */}
                {newBank.publisher === 'Diğer' && (
                  <div className="mt-2">
                    <input
                      type="text"
                      value={customPublisher}
                      onChange={(e) => setCustomPublisher(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Yayın evi adını yazın..."
                    />
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Toplam Soru Sayısı</label>
                <input
                  type="number"
                  value={newBank.totalQuestions}
                  onChange={(e) => setNewBank({...newBank, totalQuestions: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Örn: 1500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Fiyat (₺)</label>
                <input
                  type="number"
                  step="0.01"
                  value={newBank.price}
                  onChange={(e) => setNewBank({...newBank, price: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Örn: 45.90"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setCustomPublisher('');
                  setEditingBank(null);
                  setNewBank({
                    name: '',
                    subject: 'turkce',
                    publisher: 'Karekök Yayınları',
                    totalQuestions: '',
                    price: ''
                  });
                }}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={editingBank ? handleEditBank : handleAddBank}
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                {editingBank ? 'Güncelle' : 'Ekle'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionBank;
